import {
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
  useRef,
  useState,
} from 'react';

import { Icon } from '@/component/Tool/IconPopover';
import {
  Add,
  Done,
  KeyboardArrowDown,
  RGBA,
  VFlex,
  VStack,
  VTooltip,
  cn,
} from '@/package';
import { useToolMenuStore } from '@/store';
import type { ObjectClass } from '@/type';

import {
  useExtendClassWithToolHandler,
  useFilterTaskSuperClassListWithChild,
  useInitToolList,
  useRegisterShortcut,
} from './hook';

const LabelingSelectPopup = () => {
  const { filteredSuperObjectClassList } =
    useFilterTaskSuperClassListWithChild();
  useInitToolList();
  const extendClassWithToolHandler = useExtendClassWithToolHandler();
  useRegisterShortcut(extendClassWithToolHandler);
  const { findByName } = useToolMenuStore();
  const genBoxTool = findByName('box')!;

  if (!genBoxTool.isSelected) return null;
  return (
    <VStack
      className="absolute top-0 left-[47px] max-w-[200px] min-w-0 rounded-sm bg-gray-200 p-0.5 text-primary-500"
      gap={0.125}
    >
      {filteredSuperObjectClassList.map(superClass => (
        <ClassItem
          key={superClass.id}
          superClass={superClass}
          extendClassWithToolHandler={extendClassWithToolHandler}
        />
      ))}
    </VStack>
  );
};

const ClassItem = ({
  superClass,
  extendClassWithToolHandler,
}: {
  superClass: ObjectClass;
  extendClassWithToolHandler: ReturnType<typeof useExtendClassWithToolHandler>;
}) => {
  const [isExpand, setIsExpand] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const { isMoreThanOneAvaliableObjectClass } =
    useFilterTaskSuperClassListWithChild();
  const isMoreThanOne = isMoreThanOneAvaliableObjectClass(superClass);
  const { getToShowObjectClass, isSelectedObjectClass, selectObjectClassTool } =
    extendClassWithToolHandler;
  const toShowObjectClass = getToShowObjectClass(superClass);
  const icon = toShowObjectClass.icon;

  return (
    <>
      <Item
        ref={itemRef}
        className={cn({
          '!bg-primary-500': isSelectedObjectClass(toShowObjectClass),
          'bg-white text-primary-500':
            !isSelectedObjectClass(toShowObjectClass),
        })}
        style={{
          color: isSelectedObjectClass(toShowObjectClass)
            ? new RGBA(toShowObjectClass.color).toString()
            : '#0B1B3E',
        }}
      >
        <VFlex items="center" gap={0.25}>
          <VFlex
            items="center"
            gap={0.125}
            role="button"
            onClick={() => {
              selectObjectClassTool(toShowObjectClass);
            }}
          >
            <VFlex items="center" className="shrink-0">
              <Add className="size-3" />
              <Icon
                data={icon?.data ?? ''}
                color={
                  isSelectedObjectClass(toShowObjectClass)
                    ? new RGBA(toShowObjectClass.color).toString()
                    : '#0B1B3E'
                }
                size={20}
              />
            </VFlex>
            <VTooltip
              content={toShowObjectClass.alias || toShowObjectClass.name}
            >
              <div className="w-16 truncate text-body-lg">
                {toShowObjectClass.alias || toShowObjectClass.name}
              </div>
            </VTooltip>
          </VFlex>
          {isMoreThanOne && (
            <>
              <div className="h-[18px] w-px bg-gray-300" />
              <KeyboardArrowDown
                className={cn('size-4', {
                  'rotate-180': isExpand,
                  'text-white': isSelectedObjectClass(toShowObjectClass),
                  'text-primary-500': !isSelectedObjectClass(toShowObjectClass),
                })}
                role="button"
                onClick={e => {
                  e.stopPropagation();
                  setIsExpand(!isExpand);
                }}
              />
            </>
          )}
        </VFlex>
      </Item>
      {isExpand && (
        <ClassListPanel
          superClass={superClass}
          extendClassWithToolHandler={extendClassWithToolHandler}
          itemWidth={itemRef.current?.offsetWidth}
        />
      )}
    </>
  );
};

const ClassListPanel = ({
  superClass,
  itemWidth,
  extendClassWithToolHandler,
}: {
  superClass: ObjectClass;
  itemWidth?: number;
  extendClassWithToolHandler: ReturnType<typeof useExtendClassWithToolHandler>;
}) => {
  const { isSelectedObjectClass, selectObjectClassTool, isRecentObjectClass } =
    extendClassWithToolHandler;
  const { isUnavaliableSuperClass } = useFilterTaskSuperClassListWithChild();

  const sortedClassItemList = (
    isUnavaliableSuperClass(superClass)
      ? [...(superClass.extendClass ?? [])]
      : [superClass, ...(superClass.extendClass ?? [])]
  ).sort((a, b) => {
    if (isSelectedObjectClass(a)) return -1;
    if (isRecentObjectClass(a)) return -1;
    if (isSelectedObjectClass(b)) return 1;
    if (isRecentObjectClass(b)) return 1;
    return 0;
  });

  return (
    <div
      className="w-full min-w-0 overflow-hidden rounded-sm border border-gray-200 bg-white py-1"
      style={itemWidth ? { width: `${itemWidth}px` } : undefined}
    >
      {sortedClassItemList.map(classItem => (
        <VFlex
          className="w-full min-w-0 border border-gray-200 px-2 py-[2px] text-body-lg text-black"
          key={classItem.id}
          gap={0.25}
          items="center"
          role="button"
          onClick={() => {
            selectObjectClassTool(classItem);
          }}
        >
          <div className="size-4 shrink-0">
            {isSelectedObjectClass(classItem) && (
              <Done className="size-4 text-blue-300" />
            )}
          </div>
          <div className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
            {classItem.alias || classItem.name}
          </div>
        </VFlex>
      ))}
    </div>
  );
};

const Item = ({
  children,
  className,
  ref,
  ...props
}: PropsWithChildren<
  ComponentPropsWithoutRef<'div'> & {
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
  }
>) => {
  return (
    <div
      ref={ref}
      className={cn(
        'w-full min-w-0 rounded-sm bg-white px-1 py-0.5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export default LabelingSelectPopup;
